import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getAccessToken,
  onAuthChange,
  signInWithGoogle,
  signOut as supabaseSignOut,
  type AuthUser,
} from './supabase';
import { localAccountMigration } from '../account/dailyStore';

export interface UseAuth {
  user: AuthUser | null;
  loading: boolean;
  signInGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

export function useAuth(): UseAuth {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  // Track whether we have already called applyAccountMigration in this session
  // to avoid repeated migration calls on re-renders (the no-op is safe but we
  // want to be explicit: one call per authenticated session transition).
  const migratedRef = useRef(false);

  useEffect(() => {
    try {
      return onAuthChange((next) => {
        setUser(next);
        setLoading(false);
        // SEAM: on first authenticated session, attempt migration of local daily progress.
        // Today this is a no-op (localAccountMigration.applyAccountMigration returns
        // { migrated: false } and leaves local data intact). When the server foundation
        // lands, this call will POST the pending DailyProgress to the server's idempotent
        // merge endpoint and receive the authoritative balance back.
        if (next !== null && !migratedRef.current) {
          migratedRef.current = true;
          void localAccountMigration.applyAccountMigration(next.id);
        }
        // Reset migration flag when user signs out, so a subsequent login re-attempts.
        if (next === null) {
          migratedRef.current = false;
        }
      });
    } catch {
      setUser(null);
      setLoading(false);
      return () => {};
    }
  }, []);

  const signInGoogle = useCallback(() => signInWithGoogle(), []);
  const signOut = useCallback(() => supabaseSignOut(), []);
  const getToken = useCallback(() => getAccessToken(), []);

  return { user, loading, signInGoogle, signOut, getToken };
}
